import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useApiUrl } from './ApiContext';

function importAll(r) {
  let images = {};
  r.keys().forEach((key) => {
    const filename = key
      .replace("./", "")
      .toLowerCase()
      .replace(/\.jpeg$/, ".jpg")
      .replace(/\.jpg$/, ".jpg");
    images[filename] = r(key);
  });
  return images;
}

const productImages = importAll(
  require.context(
    "./assets/images/products",
    false,
    /\.(png|jpe?g|webp|svg)$/i
  )
);

const normalize = (s) =>
  s
    ? s
        .replace(/\s+/g, " ")
        .replace(/\u00A0/g, " ")
        .replace(/^\s+|\s+$/g, "")
        .toLowerCase()
        .replace(/\.jpeg$/, ".jpg")
        .replace(/\.jpe$/, ".jpg")
    : "";

const Compare = () => {
  const apiUrl = useApiUrl();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${apiUrl}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, [apiUrl]);

  useEffect(() => {
    const storedProducts = JSON.parse(localStorage.getItem('selectedProducts')) || [];
    setSelectedProducts(storedProducts);
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('selectedProducts', JSON.stringify(selectedProducts));
    }
  }, [selectedProducts, isInitialized]);

  const getProductImage = (product) => {
    const matchedKey = Object.keys(productImages).find(
      (key) => key === normalize(product.imageUrl)
    );
    return productImages[matchedKey];
  };

  // Switch to uid for identity; keep name fallback for legacy objects
  const bySame = (a, b) => (a?.uid && b?.uid) ? a.uid === b.uid : a?.name === b?.name;

  const handleProductRemove = (productUidOrName) => {
    const updatedProducts = selectedProducts.filter((p) =>
      p?.uid ? p.uid !== productUidOrName : p.name !== productUidOrName
    );
    setSelectedProducts(updatedProducts);
  };

  const handleProductSelect = (product) => {
    if (selectedProducts.length < 3) {
      // prevent duplicates by uid/name
      if (!selectedProducts.some((p) => bySame(p, product))) {
        const updatedProducts = [...selectedProducts, product];
        setSelectedProducts(updatedProducts);
        setSearchTerm('');
      }
    }
  };

  const handleSearchChange = (value) => setSearchTerm(value);

  const filteredProducts = () => {
    const search = searchTerm.toLowerCase();
    return products.filter(
      (product) =>
        product.name.toLowerCase().includes(search) &&
        !selectedProducts.some((sp) => bySame(sp, product))
    );
  };

  const handleEnquire = (productName) => {
    navigate(`/products?name=${encodeURIComponent(productName)}`);
  };

  const formatFieldLabel = (key) => {
    const labelMap = {
      sowingTime: 'Sowing Time',
      lasts: 'Lasts',
      grazingPeriod: 'Grazing Period',
      crudeProtein: 'Crude Protein',
      dm: 'Dry Matter (DM)',
      faoEstimate: 'FAO Estimate',
      cobRipenessScore: 'COB Ripeness Score',
      maturityGroup: 'Maturity Group',
      size: 'Size',
    };
    return labelMap[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  return (
    <div className="compare-container">
      <h1>Compare Products</h1>
      <div className="compare-cards">
        {selectedProducts.map((product) => (
          <div key={product.uid || product.name} className="compare-card">
            <div className="compare-product-info">
              <button
                onClick={() => handleProductRemove(product.uid || product.name)}
                className="compare-remove-btn"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <img src={getProductImage(product)} alt={product.name} />
              <h2>{product.name}</h2>
              {product.description ? (
                <p>{product.description}</p>
              ) : (
                <table className="product-info-table">
                  <tbody>
                    {Object.entries(product.additionalDetails || {}).map(([key, value]) =>
                      value && (
                        <tr key={key}>
                          <td>{formatFieldLabel(key)}</td>
                          <td>{value}</td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              )}
              <button
                onClick={() => handleEnquire(product.name)}
                className="find-enquire-btn"
              >
                Enquire
              </button>
            </div>
          </div>
        ))}

        {selectedProducts.length < 3 && (
          <div className="compare-card compare-placeholder">
            <input
              type="text"
              placeholder="Search for a product..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            {searchTerm && (
              <div className="compare-search-results-container">
                <div className="compare-search-results">
                  {filteredProducts().map((product) => (
                    <div
                      key={product.uid || product.name}
                      className="compare-search-result"
                      onClick={() => handleProductSelect(product)}
                    >
                      <img
                        src={getProductImage(product)}
                        alt={product.name}
                        className="compare-search-result-image"
                      />
                      <h4>{product.name}</h4>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Compare;
